// The sheet's drag handle (ui-map-interface.md §12.0). A bottom sheet that
// only answers a tab press is a sheet with one size the hand cannot argue
// with, so the grip at its top edge takes the sheet to any height. It rests
// at one of the three stops when the finger let go near one (R570).
//
// This file is in two halves, and the split is the same one `ui/touch.ts`
// makes. `SheetDrag` is the whole rule and it is pure: no DOM, no canvas, no
// clock — the caller passes coordinates in and reads a height out. The
// installer under it is the thin part that a browser needs, and it holds no
// rule of its own.
//
// **The resize never rides `transform`.** The chrome dock-in owns that
// property for every panel that travels (§3.2), and `dockin.test.ts` fails
// the stylesheet for a bare `transform` on `#dock`. The height therefore
// travels as a custom property the sheet's `max-height` reads.
//
// **The installer reads the finger in chrome px** (R1556). The sheet's
// heights are chrome px, and a pointer is viewport px. At a UI scale of
// 140 %, a pointer read in viewport px would move the sheet 1.4 times as
// far as the finger.

import { toChromePx } from './uiscale';

/** The three sizes a sheet settles at. */
export type SheetSnap = 'collapsed' | 'rest' | 'expanded';

/** Where a release leaves the sheet (R570): one of the three stops, or the
 *  free height the finger let go at. The two are one type, because
 *  `applySheet()` in the overlay is the one place that answers the question
 *  "how tall is the sheet" and it must answer it from one value. */
export type SheetSettle =
  | { kind: 'snap'; snap: SheetSnap }
  | { kind: 'free'; height: number };

/** The order the snaps stand in, smallest first. A tie in the nearest-stop
 *  test therefore takes the smaller sheet: less of the map is covered. */
export const SHEET_SNAPS: readonly SheetSnap[] = ['collapsed', 'rest', 'expanded'];

/** How tall the sheet is at each of its three stops, in CSS pixels. The
 *  caller measures them, because every one of the three is a question for
 *  the layout engine and not a constant. */
export type SheetStops = Record<SheetSnap, number>;

export interface SheetDragOptions {
  /** Travel below which the press was a press and not a drag. A finger is
   *  never still, and a grip that resized on a tap would be a trap. */
  slopPx: number;
  /** How near a stop a release must end for that stop to take it (R570).
   *  One figure for all three stops: a band is a fingertip's reach, and a
   *  fingertip is the same size at every stop. 36 px is a little under the
   *  40 px touch floor (§12.0), so the band a finger meant to hit is the
   *  band it hits, and a band never reaches half the way to its neighbour —
   *  a band that did would be the nearest-stop rule again. */
  snapPx: number;
}

export const SHEET_DRAG_DEFAULTS: SheetDragOptions = { slopPx: 8, snapPx: 36 };

/**
 * The grip's state machine.
 *
 * The rules, and why each one is here:
 *
 * - The sheet grows as the finger goes **up**, because the sheet stands on
 *   the bottom edge of the screen. Screen y grows downward, so the travel
 *   is subtracted.
 * - The live height is clamped to the two outer stops. A finger that runs
 *   off the top of the screen must not ask for a sheet taller than the
 *   screen.
 * - A press that never passes the slop settles back where it started. The
 *   grip sits over the sheet's own content, and a tap must not resize it.
 * - A release settles at a stop only when it ends inside that stop's snap
 *   band (R570). Everywhere else the sheet rests at the height the finger
 *   left it. The three stops are the sizes worth aiming for; they are not
 *   the only sizes a sheet may have, and a player who drags the sheet to
 *   two thirds of the screen asked for two thirds of the screen.
 * - There is no velocity term: a flick that lands between two stops has
 *   said nothing the position does not already say.
 */
export class SheetDrag {
  private down = false;
  private moved = false;
  private startY = 0;
  private startH = 0;
  private from: SheetSnap = 'rest';
  private live = 0;

  constructor(readonly opts: SheetDragOptions = SHEET_DRAG_DEFAULTS) {}

  /** True while a finger is on the grip. */
  get dragging(): boolean { return this.down; }
  /** True once that finger has travelled past the slop. */
  get moving(): boolean { return this.moved; }
  /** The height the sheet should be drawn at now. */
  get height(): number { return this.live; }
  /** The stop the drag started from. */
  get origin(): SheetSnap { return this.from; }

  /** A finger came down on the grip. `height` is the sheet's height now. */
  begin(y: number, height: number, from: SheetSnap) {
    this.down = true;
    this.moved = false;
    this.startY = y;
    this.startH = height;
    this.from = from;
    this.live = height;
  }

  /** The finger moved. Returns the height the sheet should take. */
  move(y: number, stops: SheetStops): number {
    if (!this.down) return this.live;
    if (Math.abs(y - this.startY) > this.opts.slopPx) this.moved = true;
    this.live = clamp(this.startH + (this.startY - y), stops);
    return this.live;
  }

  /**
   * The finger lifted. Returns where the sheet settles, and ends the drag.
   * A press that never became a drag settles back at its origin.
   */
  end(y: number, stops: SheetStops): SheetSettle {
    if (!this.down) return { kind: 'snap', snap: this.from };
    this.move(y, stops);
    const moved = this.moved;
    const height = this.live;
    this.down = false;
    this.moved = false;
    if (moved) return settle(height, stops, this.opts.snapPx);
    this.live = this.startH;
    return this.back(stops);
  }

  /** The browser took the pointer away. The sheet goes back where it was —
   *  a free height too, if that is where it stood (R570). The stops are the
   *  drag's own, because a cancel with no stops to measure against can only
   *  name the stop the drag began at. */
  cancel(stops: SheetStops | null): SheetSettle {
    this.down = false;
    this.moved = false;
    this.live = this.startH;
    return stops ? this.back(stops) : { kind: 'snap', snap: this.from };
  }

  /** Where the drag began, as a settle result. It goes through the same
   *  band as a release does: the sheet's measured height is never exactly
   *  the nominal height of a stop, so a press that resettled on the raw
   *  figure would pin a sheet that had asked for nothing (R570). */
  private back(stops: SheetStops): SheetSettle {
    return settle(this.startH, stops, this.opts.snapPx);
  }
}

/**
 * Where a release leaves the sheet (R570).
 *
 * A stop takes the release when the sheet ends inside that stop's band.
 * Outside every band the sheet keeps the height it has.
 */
export function settle(h: number, stops: SheetStops, snapPx: number): SheetSettle {
  const snap = nearest(h, stops);
  return Math.abs(stops[snap] - h) <= snapPx
    ? { kind: 'snap', snap }
    : { kind: 'free', height: h };
}

/** Holds a height inside the outer two stops. */
function clamp(h: number, stops: SheetStops): number {
  const lo = Math.min(stops.collapsed, stops.rest, stops.expanded);
  const hi = Math.max(stops.collapsed, stops.rest, stops.expanded);
  return Math.min(hi, Math.max(lo, h));
}

/** The stop a height is closest to. A tie takes the smaller sheet. */
export function nearest(h: number, stops: SheetStops): SheetSnap {
  let best: SheetSnap = SHEET_SNAPS[0];
  let bestD = Infinity;
  for (const s of SHEET_SNAPS) {
    const d = Math.abs(stops[s] - h);
    if (d < bestD) { bestD = d; best = s; }
  }
  return best;
}

// --- the installer -------------------------------------------------------

/** What the grip needs from the shell around it. */
export interface SheetDragHost {
  /** The grip element itself. It takes the pointer capture, so a finger
   *  that leaves the strip keeps driving the drag. */
  grip: HTMLElement;
  /** The three stops, measured at the moment a drag begins. */
  stops: () => SheetStops;
  /** The sheet's height now, and the stop it stands at. */
  at: () => { snap: SheetSnap; height: number };
  /** The live height during a drag; `null` clears the override again. */
  live: (px: number | null) => void;
  /** Where the sheet settles at release: a stop, or a free height. */
  settle: (at: SheetSettle) => void;
}

/**
 * Puts the state machine on a grip element. Returns the teardown, which the
 * overlay calls when the form factor changes back or the overlay is
 * destroyed — the shell installs this only in the sheet form factor.
 */
export function installSheetDrag(
  host: SheetDragHost,
  opts: SheetDragOptions = SHEET_DRAG_DEFAULTS,
): () => void {
  const drag = new SheetDrag(opts);
  let stops: SheetStops | null = null;
  /** The pressing pointer, or null. A pointer id can be negative. */
  let id: number | null = null;

  const down = (ev: PointerEvent) => {
    if (ev.button > 0 || drag.dragging) return;
    ev.preventDefault(); // the grip is a control, not a place to select text
    const here = host.at();
    stops = host.stops();
    id = ev.pointerId;
    drag.begin(toChromePx(ev.clientY), here.height, here.snap);
    host.live(here.height);
    // A capture keeps the move and the up on this element after the finger
    // has left the 28 px strip, which it does immediately in any real drag.
    try { host.grip.setPointerCapture(id); } catch { /* jsdom, and old Safari */ }
  };

  const move = (ev: PointerEvent) => {
    if (!drag.dragging || ev.pointerId !== id || !stops) return;
    host.live(drag.move(toChromePx(ev.clientY), stops));
  };

  const up = (ev: PointerEvent) => {
    if (!drag.dragging || ev.pointerId !== id || !stops) return;
    finish(drag.end(toChromePx(ev.clientY), stops));
  };

  const cancel = (ev: PointerEvent) => {
    if (!drag.dragging || ev.pointerId !== id) return;
    finish(drag.cancel(stops));
  };

  /** The end of one press: the live height goes, the sheet settles. */
  const finish = (at: SheetSettle) => {
    host.live(null);
    host.settle(at);
    if (id !== null) {
      try { host.grip.releasePointerCapture(id); } catch { /* never captured */ }
    }
    id = null;
    stops = null;
  };

  host.grip.addEventListener('pointerdown', down);
  host.grip.addEventListener('pointermove', move);
  host.grip.addEventListener('pointerup', up);
  host.grip.addEventListener('pointercancel', cancel);
  return () => {
    host.grip.removeEventListener('pointerdown', down);
    host.grip.removeEventListener('pointermove', move);
    host.grip.removeEventListener('pointerup', up);
    host.grip.removeEventListener('pointercancel', cancel);
    if (drag.dragging) { drag.cancel(stops); host.live(null); }
  };
}
